import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryFlightSelectorData = Content.Components['FlightSelectorModuleDto'];

interface UseQueryFlightSelectorOptions {
  initialData?: UseQueryFlightSelectorData;
}

interface UseQueryFlightSelectorParameters {
  adultsNo?: number | null;
  adultsNoPerRoom?: string | null;
  boardTypes?: string | null;
  bonusCodes?: string | null;
  childrenAges?: string | null;
  childrenAgesPerRoom?: string | null;
  departureDate?: string;
  departureId?: number;
  durationRange?: string | null;
  ex?: string | null;
  hotelId?: number;
  id: string;
  offerId?: string;
  pageId: string;
  periodType?: Content.Components['PeriodType'] | null;
}

const useQueryFlightSelector = (
  parameters: UseQueryFlightSelectorParameters,
  options?: UseQueryFlightSelectorOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQueryFlightSelectorData>(
    [QueryId.FLIGHT_SELECTOR, parameters, locale],
    async () => {
      const baseUrl = '/api/content-modules/flight-selector';
      const queryParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${queryParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryFlightSelector', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryFlightSelector;
